import { Component, Prop, h, Event, Listen, EventEmitter, State } from '@stencil/core';
import { Option, FacetUpdateEvent, FacetChangeEvent, FacetItem, getData } from '@hpfc/fc-frontend-core';

/**
 * A list of radio buttons within a fieldset.
 *
 * The radio button data can be passed in as a `Prop`, or using an `fccdComponentEvent`.
 */
@Component({
  tag: 'fccd-radio-list',
})
export class RadioList {
  /**
   * Unique identifier for the element.
   *
   * Also used to identify the element in the fccdFacetChangeEvent event model.
   */
  @Prop() componentName!: string;

  /**
   * Optional message for the fieldset.
   */
  @Prop() label?: string;

  /**
   * Size of the fieldset message.
   */
  @Prop() labelSize: 'lg' | 'md' | 'sm' | 'xs' = 'xs';

  /**
   * Type of the fieldset message.
   */
  @Prop() labelType: 'body' | 'heading' = 'heading';

  /**
   * The initial value for the dropdown options. This Prop can be overwritten by data coming from an fccdComponentEvent.
   *
   * The options can be passed in as a JSON string, or an array of Option objects.
   */
  @Prop() options: string | Array<Option>;

  /**
   * The amount of vertical space to add above the radio button
   */
  @Prop() verticalSpacing: 'lg' | 'md' | 'sm' | 'xs' | 0 = 0;

  /**
   * Hide the radio button
   */
  @State() hidden: boolean = false;

  /**
   * Selected value
   */
  @State() selected: string;

  /*
   * Dropdown options to display.
   */
  @State() data: Array<Option>;

  /**
   * A map of the radio buttons.
   */
  @State() refs: Map<string, HTMLInputElement> = new Map();

  /**
   * Dispatches this event when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  /**
   * Sets the initial value for the radio button options
   */
  componentWillLoad() {
    if (!this.data && this.options) {
      this.data = getData(this.options);
    }
  }

  /**
   * Handles the selection of a radio button.
   */
  @Listen('hp5ValidationChange')
  selectHandler(event: CustomEvent) {
    if (!this.data) {
      return;
    }

    const changedValue = event.detail.value;

    // Update the data state
    const optionToUpdate = this.data.find(option => option.value === changedValue);
    if (optionToUpdate) {
      optionToUpdate.isSelected = true;
    }

    // Clear all that aren't the match
    this.data.forEach(option => {
      if (option.value !== changedValue) {
        option.isSelected = false;
      }
    });

    // Update all radio buttons
    this.data.forEach(option => {
      const ref = this.refs.get(this.componentName + option.value);
      if (ref) {
        ref.checked = option.isSelected;
      }
    });

    // Dispatch the event
    const facetItem = new FacetItem(this.componentName, changedValue, true);
    this.fccdFacetChangeEvent.emit(new FacetChangeEvent(event, facetItem));
  }

  /**
   * Updates the radio buttons when new data is received.
   *
   * @param event The event containing the new data.
   */
  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    const hasNewData = event?.detail?.facetOptionMap?.has(this.componentName);
    if (!hasNewData) {
      return;
    }
    this.data = event.detail.facetOptionMap.get(this.componentName);

    const facetItem = event?.detail?.facetMap?.get(this.componentName);
    if (facetItem) {
      // we want to hide the radio list if the facet is marked as disabled
      this.hidden = facetItem.disabled || false;
      this.selected = facetItem.selected ? facetItem.value : null;

      this.callback = (() => {
        this.data.forEach(option => {
          const name = this.componentName + option.value;
          const el = this.refs.get(name);
          if (el) {
            el.checked = option.value === this.selected;
          }
        });
      }).bind(this);
    }
  }

  /**
   * Determines if the radio list is visible.
   * @returns `true` if the radio list is visible, `false` otherwise.
   */
  isVisible = (): boolean => Boolean(this.data && this.data.length > 0 && !this.hidden);

  private callback: () => void;

  componentDidRender() {
    if (this.callback) {
      this.callback();
      this.callback = null;
    }
  }

  render() {
    if (!this.isVisible()) {
      return null;
    }
    return (
      <div>
        <hp5-spacing-vertical xs={this.verticalSpacing} />
        <hp5-fieldset legend-message={this.label} lengend-size={this.labelSize} legend-type={this.labelType} disabled={this.hidden}>
          {this.data.map(option => (
            <hp5-input-radio-button
              slot="form-elements"
              ref={(el: any) => this.refs.set(this.componentName + option.value, el)}
              element-id={this.componentName + option.value}
              label={option.label}
              name={this.componentName + option.value}
              required={true}
              value={option.value}
            />
          ))}
        </hp5-fieldset>
      </div>
    );
  }
}
